import { useEffect, useState } from "react";
import DashboardService from "../../../api/services/dashboard.service";
import { AnalyticsData } from "../../../api/types/analytics.types";
import RevenueChart from "./RevenueChart";
import OrdersChart from "./OrdersChart";
import StatusDistributionChart from "./StatusDistributionChart";
import Loading from "../../common/loading/Loading";
import { useTranslation } from "react-i18next";
import styles from "./ChartsSection.module.scss";

const ChartsSection = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation("translation");

  const fetchAnalytics = async () => {
    try {
      setError(null);
      setLoading(true);
      const response = await DashboardService.getAnalyticsData(30); // Last 30 days
      setAnalyticsData(response.data);
    } catch (err) {
      console.error("Failed to fetch analytics data:", err);
      setError(
        t("charts.load_error") ||
          "Failed to load analytics data. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <section className={styles.chartsSection}>
        <h2 className={styles.sectionTitle}>
          {t("charts.section_title") || "Analytics"}
        </h2>
        <div style={{ padding: "24px", textAlign: "center", color: "#888" }}>
          <p style={{ marginBottom: "16px" }}>{error}</p>
          <button
            type="button"
            onClick={fetchAnalytics}
            style={{
              padding: "8px 16px",
              backgroundColor: "#1a2b7f",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            {t("buttons.retry") || "Retry"}
          </button>
        </div>
      </section>
    );
  }

  if (!analyticsData) {
    return null;
  }

  return (
    <section className={styles.chartsSection}>
      <h2 className={styles.sectionTitle}>
        {t("charts.section_title") || "Analytics"}
      </h2>
      <div className={styles.chartsGrid}>
        <div className={styles.chartWrapper}>
          <RevenueChart
            data={analyticsData.revenue_by_date}
            currency={analyticsData.currency}
          />
        </div>
        <div className={styles.chartWrapper}>
          <OrdersChart data={analyticsData.orders_by_date} />
        </div>
        <div className={styles.chartWrapper}>
          <StatusDistributionChart
            data={analyticsData.order_status_distribution}
          />
        </div>
      </div>
    </section>
  );
};

export default ChartsSection;
